import { useCallback } from 'react';

import { useAuth } from 'modules/authentication/presentation/contexts';
import { HTTPError, UseCaseError } from 'shared/domain/errors';
import { useLocale, useToast } from 'shared/presentation/contexts';

import useRegisterError from './useRegisterError';

type TLocaleNamespace = Parameters<typeof useLocale>[0];
type THandleErrorOptions = { toast?: boolean; registerErrorKey?: string };

export default function useRequestErrorHandler(namespace: TLocaleNamespace) {
  const toast = useToast();
  const errorRegister = useRegisterError();
  const auth = useAuth();

  const { t, sharedT } = useLocale(namespace);

  const getErrorMessage = useCallback(
    (error: unknown) => {
      if (error instanceof UseCaseError) {
        return {
          type: 'DEFAULT_ERROR',
          message: error.translateMessage(t as (text: string) => string),
        };
      }

      if (error instanceof HTTPError) {
        return {
          type: error.type,
          message: error.message,
        };
      }

      if (error instanceof Error) {
        return {
          type: 'DEFAULT_ERROR',
          message: error.message,
        };
      }
    },
    [t],
  );

  const handleError = useCallback(
    (
      error: unknown,
      { registerErrorKey, toast: shouldToast = true }: THandleErrorOptions = {},
    ) => {
      if (registerErrorKey) {
        errorRegister.register({
          key: registerErrorKey,
          error,
        });
      }

      if (error instanceof HTTPError && error.unauthorized) {
        return auth.logout();
      }

      if (!shouldToast) return;

      const err = getErrorMessage(error);

      toast.error(
        err?.message || sharedT('Error, something unexpected happened'),
      );
    },
    [errorRegister, toast, auth, getErrorMessage, sharedT],
  );

  return { handleError, getErrorMessage };
}
